import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Table, Button, Space, Tag, Modal, Form, Input, Select, Switch, App, Collapse, Tooltip, Popconfirm, theme } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SendOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import apiClient from '../api/client';

const { Title, Text } = Typography;

// Longest readable measure for the prose inside the setup guide. Wider than
// this and the eye loses the line it is on.
const PROSE_WIDTH = 780;

interface Webhook {
  id: string;
  url: string;
  type: string;
  events: string[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

const EVENT_OPTIONS = [
  { label: 'Any State Change', value: 'STATE_CHANGE' },
  { label: 'DOWN', value: 'DOWN' },
  { label: 'ERROR', value: 'ERROR' },
  { label: 'ZOMBIE', value: 'ZOMBIE' },
  { label: 'DEGRADED', value: 'DEGRADED' },
  { label: 'SUSPECT', value: 'SUSPECT' },
  { label: 'HEALTHY', value: 'HEALTHY' },
];

const EVENT_COLORS: Record<string, string> = {
  STATE_CHANGE: 'blue',
  DOWN: 'red',
  ERROR: 'red',
  ZOMBIE: 'purple',
  DEGRADED: 'gold',
  SUSPECT: 'yellow',
  HEALTHY: 'green',
};

const WebhooksPage: React.FC = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const fetchWebhooks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/webhooks');
      setWebhooks(data.data || []);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ type: 'slack', events: ['STATE_CHANGE'] });
    setModalOpen(true);
  };

  const handleEdit = (wh: Webhook) => {
    setEditingId(wh.id);
    form.setFieldsValue({ url: wh.url, type: wh.type, events: wh.events });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await apiClient.put(`/webhooks/${editingId}`, values);
        message.success('Webhook updated');
      } else {
        await apiClient.post('/webhooks', values);
        message.success('Webhook created');
      }
      setModalOpen(false);
      fetchWebhooks();
    } catch (err: any) {
      if (err.response) {
        message.error(err.response.data?.error || 'Failed to save webhook');
      }
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/webhooks/${id}`);
      message.success('Webhook deleted');
      fetchWebhooks();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete webhook');
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      await apiClient.put(`/webhooks/${id}`, { enabled });
      fetchWebhooks();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update webhook');
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    try {
      const { data } = await apiClient.post(`/webhooks/${id}/test`);
      if (data.data?.success) {
        message.success(`Test sent (HTTP ${data.data.statusCode})`);
      } else {
        message.warning(`Test returned HTTP ${data.data?.statusCode}`);
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Test failed');
    } finally {
      setTestingId(null);
    }
  };

  const maskUrl = (url: string) => {
    try {
      const u = new URL(url);
      const path = u.pathname.length > 20 ? u.pathname.slice(0, 20) + '...' : u.pathname;
      return u.host + path;
    } catch {
      return url.slice(0, 40) + '...';
    }
  };

  const columns = [
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      // A webhook URL is an identifier: it truncates with an ellipsis and keeps
      // the full value one hover away, never wrapping onto a second line.
      render: (url: string) => <Text ellipsis={{ tooltip: url }} style={{ fontSize: 13 }}>{maskUrl(url)}</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (t: string) => (
        <Tag color={t === 'slack' ? 'geekblue' : 'default'} style={{ margin: 0, fontSize: 12 }}>{t.toUpperCase()}</Tag>
      ),
    },
    {
      // Up to three events inline, the tail folded into "+N". The tooltip
      // always carries the complete list, so a 7-event hook still fits one row.
      title: 'Events',
      dataIndex: 'events',
      key: 'events',
      width: 250,
      render: (events: string[]) => {
        const list = events || [];
        const head = list.slice(0, 3);
        const rest = list.slice(3);
        return (
          <Tooltip title={list.join(', ') || 'none'}>
            <Space size={2} wrap={false}>
              {head.map((e) => (
                <Tag key={e} color={EVENT_COLORS[e] || 'default'} style={{ margin: 0, fontSize: 12, lineHeight: '18px' }}>{e}</Tag>
              ))}
              {rest.length > 0 && <Tag style={{ margin: 0, fontSize: 12, lineHeight: '18px' }}>+{rest.length}</Tag>}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      align: 'center' as const,
      render: (enabled: boolean, record: Webhook) => (
        <Switch size="small" aria-label={enabled ? "Disable webhook" : "Enable webhook"} checked={enabled} onChange={(v) => handleToggle(record.id, v)} />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      align: 'right' as const,
      render: (_: any, record: Webhook) => (
        <Space size={2}>
          <Tooltip title="Send a test delivery">
            <Button size="small" type="text" aria-label="Send a test delivery" icon={<SendOutlined />} loading={testingId === record.id} onClick={() => handleTest(record.id)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" type="text" aria-label="Edit webhook" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          {/* Deleting silently stops alerts reaching a channel — confirm it. */}
          <Popconfirm
            title="Delete this webhook?"
            description="Alerts will stop being delivered to it."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Delete">
              <Button size="small" type="text" danger aria-label="Delete webhook" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const typeValue = Form.useWatch('type', form);

  return (
    <>
      <Space style={{ marginBottom: 8, justifyContent: 'space-between', width: '100%' }}>
        <Space size={8} align="baseline">
          <Title level={4} style={{ margin: 0 }}>Webhooks</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>{webhooks.length} total</Text>
        </Space>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Add Webhook
        </Button>
      </Space>

      <Collapse
        size="small"
        style={{ marginBottom: 10 }}
        items={[{
          key: 'guide',
          label: <span style={{ fontSize: 13 }}><QuestionCircleOutlined style={{ marginRight: 6 }} />Setup Guide & Examples</span>,
          children: (
            <div style={{ fontSize: 13, lineHeight: 1.6, maxWidth: PROSE_WIDTH }}>
              <Typography.Title level={5} style={{ marginTop: 0 }}>Slack Webhooks</Typography.Title>
              <ol style={{ paddingLeft: 18, margin: '4px 0 12px' }}>
                <li>In Slack, go to <b>Apps &rarr; Incoming Webhooks &rarr; Add New Webhook</b></li>
                <li>Choose a channel and copy the webhook URL</li>
                <li>Click <b>Add Webhook</b> above, set Type = <Tag color="geekblue" style={{ margin: 0 }}>SLACK</Tag>, paste the URL</li>
                <li>Select trigger events and click <b>Create</b></li>
                <li>Click <b>Test</b> to verify the message arrives in your Slack channel</li>
              </ol>
              <Typography.Text type="secondary">
                Slack URL format: <code>https://hooks.slack.com/services/T.../B.../xxx</code>
              </Typography.Text>

              <Typography.Title level={5} style={{ marginTop: 16 }}>Generic Webhooks</Typography.Title>
              <Typography.Paragraph style={{ margin: '4px 0 8px' }}>
                Sends a JSON POST to any HTTP endpoint when a trigger event fires.
              </Typography.Paragraph>
              <Typography.Text strong>Payload example:</Typography.Text>
              <pre style={{
                background: token.colorFillQuaternary,
                border: `1px solid ${token.colorBorderSecondary}`,
                padding: '8px 12px',
                borderRadius: token.borderRadius,
                fontSize: 12,
                lineHeight: 1.5,
                overflowX: 'auto',
                margin: '6px 0 12px',
              }}>{`{
  "event": "state_change",
  "serverKey": "my-proxy-us-1",
  "ip": "203.0.113.1",
  "prevState": "HEALTHY",
  "state": "DOWN",
  "latency": 0,
  "timestamp": "2026-03-17T12:00:00Z"
}`}</pre>

              <Typography.Title level={5} style={{ marginTop: 4 }}>HMAC Signature Verification</Typography.Title>
              <Typography.Paragraph style={{ margin: '4px 0 8px' }}>
                If a secret is set, the request includes <code>X-Webhook-Signature: sha256=&lt;hex&gt;</code> header.
              </Typography.Paragraph>
              <pre style={{
                background: token.colorFillQuaternary,
                border: `1px solid ${token.colorBorderSecondary}`,
                padding: '8px 12px',
                borderRadius: token.borderRadius,
                fontSize: 12,
                lineHeight: 1.5,
                overflowX: 'auto',
                margin: '6px 0 12px',
              }}>{`# Python verification example
import hmac, hashlib

def verify(body: bytes, signature: str, secret: str) -> bool:
    expected = 'sha256=' + hmac.new(
        secret.encode(), body, hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(signature, expected)`}</pre>

              <Typography.Title level={5} style={{ marginTop: 4 }}>Trigger Events</Typography.Title>
              <Space size={[6, 6]} wrap style={{ marginBottom: 4 }}>
                <Tag color="blue">STATE_CHANGE</Tag> <Typography.Text type="secondary">any state transition</Typography.Text>
                <Tag color="red">DOWN</Tag> <Typography.Text type="secondary">server unreachable</Typography.Text>
                <Tag color="red">ERROR</Tag> <Typography.Text type="secondary">high packet loss</Typography.Text>
                <Tag color="purple">ZOMBIE</Tag> <Typography.Text type="secondary">locally dead, globally visible</Typography.Text>
                <Tag color="gold">DEGRADED</Tag> <Typography.Text type="secondary">high latency / some loss</Typography.Text>
                <Tag color="yellow">SUSPECT</Tag> <Typography.Text type="secondary">single failure, not confirmed</Typography.Text>
                <Tag color="green">HEALTHY</Tag> <Typography.Text type="secondary">server recovered</Typography.Text>
              </Space>

              <Typography.Title level={5} style={{ marginTop: 12 }}>Retry Policy</Typography.Title>
              <Typography.Paragraph style={{ margin: 0 }}>
                On HTTP 5xx or timeout: 1 retry after 5 seconds. No retry on 4xx errors.
              </Typography.Paragraph>
            </div>
          ),
        }]}
      />

      <Table
        dataSource={webhooks}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No webhooks yet — add one to get Slack or HTTP alerts when a server changes state.' }}
      />

      <Modal
        title={editingId ? 'Edit Webhook' : 'Add Webhook'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingId ? 'Save' : 'Create'}
      >
        <Form form={form} size="small" layout="vertical">
          <Form.Item name="url" label="Webhook URL" rules={[{ required: true, type: 'url', message: 'Enter a valid URL' }]}>
            <Input placeholder="https://hooks.slack.com/services/..." />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select options={[
              { label: 'Slack', value: 'slack' },
              { label: 'Generic', value: 'generic' },
            ]} />
          </Form.Item>
          <Form.Item name="events" label="Trigger Events" rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one event' }]}>
            <Select mode="multiple" options={EVENT_OPTIONS} placeholder="Select events..." />
          </Form.Item>
          {typeValue === 'generic' && (
            <Form.Item name="secret" label="Secret (HMAC-SHA256)">
              <Input.Password placeholder="Optional signing secret" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default WebhooksPage;
